import React, { useState, useMemo } from 'react';
import { Box, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useOverviewDataQuery, useAdultnChildOverviewDataQuery, useGenderOverviewDataQuery, useGetAddonByDeviceQuery } from '../../services/api';
import { useSelector } from 'react-redux';
import OverviewHeader from './OverviewHeader';
import OverviewContent from './OverviewContent';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import OverviewFilterBox from './OverviewFilterBox';
import OverviewLabel from './OverviewLabel';
import useGroupOptions from '../../hooks/useGroupOptions';
import useSelectedGroup from '../../hooks/useSelectedGroup';

const Overview = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { role, company } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    const [tabValue, setTabValue] = useState(0);

    // Compnay, Gorup and device options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    // API
    const { data: addonData } = useGetAddonByDeviceQuery(selectedSensor, { skip: !selectedSensor });
    const { data: hourlyData, isLoading: hourlyLoading, error: hourlyError } = useOverviewDataQuery({ mac: selectedSensor }, { skip: tabValue !== 0 || !selectedSensor });
    const { data: adultChildData, isLoading: adultChildLoading, error: adultChildError } = useAdultnChildOverviewDataQuery({ mac: selectedSensor }, { skip: tabValue !== 1 || !selectedSensor });
    const { data: genderData, isLoading: genderLoading, error: genderError } = useGenderOverviewDataQuery({ mac: selectedSensor }, { skip: tabValue !== 2 || !selectedSensor });

    const overviewData = tabValue === 0 ? hourlyData : tabValue === 1 ? adultChildData : tabValue === 2 ? genderData : null;
    const overviewLoading = tabValue === 0 ? hourlyLoading : tabValue === 1 ? adultChildLoading : tabValue === 2 ? genderLoading : null;
    const error = tabValue === 0 ? hourlyError : tabValue === 1 ? adultChildError : tabValue === 2 ? genderError : null;

    const handleTabChange = (newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box m={isSmallScreen ? '0.5rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                // Mobile Layout
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12}>
                        <OverviewHeader isSmallScreen />
                        <OverviewLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <OverviewFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <OverviewContent
                            selectedSensor={selectedSensor}
                            overviewData={overviewData}
                            overviewLoading={overviewLoading}
                            error={error}
                            isSmallScreen
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                </Grid>
            ) : (
                // Desktop Layout
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12} md={10}>
                        <OverviewHeader isSmallScreen={false} />
                        <OverviewLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={false} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                        <OverviewContent
                            selectedSensor={selectedSensor}
                            overviewData={overviewData}
                            overviewLoading={overviewLoading}
                            error={error}
                            isSmallScreen={false}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <OverviewFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={false}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Overview;
